import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/shadcn/ui/button';
import { Switch } from '@/components/shadcn/ui/switch';
import {
  type RealtimeFormData,
  realtimeFormDefaultValues,
  realtimeFormOptions,
  useCreateRealtimeMutation,
  useUpdateRealtimeMutation,
} from '@/entities/(notification)/realtime';
import { enableNotification } from '@/entities/notification';
import { errorHandler } from '@/error-handler';
import { RealtimeFormContent } from '@/features/Workflow/realtime';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useAppForm } from '@/shared/ui/form';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import {
  WORKFLOW_CHANNEL_TYPE,
  type WORKFLOW_REALTIME_BASIS,
  WORKFLOW_TYPE,
} from '@/types/workflowConst';

import useWorkflowDetail from '../useNotiDetail';

type Props = {
  initialState?: RealtimeWorkflow;
  mode: EDIT_MODE;
};

const RealtimeWorkflowDetail = ({ initialState, mode }: Props) => {
  const wr = useWorkspaceRouter();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceId = currWorkspace?.alias ?? '';
  const [enable, setEnable] = useState(initialState?.enable ?? true);

  const createMutation = useCreateRealtimeMutation();
  const updateMutation = useUpdateRealtimeMutation();

  const { onDelete } = useWorkflowDetail({
    mode,
    alias: initialState?.alias,
    initChange() {
      // no-op
    },
  });

  // Workflow 데이터를 form 데이터로 변환
  const getDefaultValues = (): RealtimeFormData => {
    if (!initialState) return realtimeFormDefaultValues;

    const { realtime, channel } = initialState;
    // WorkflowChannel을 form schema의 channel 타입으로 변환
    const formChannel: RealtimeFormData['channel'] =
      channel?.type === WORKFLOW_CHANNEL_TYPE.SLACK
        ? {
            type: WORKFLOW_CHANNEL_TYPE.SLACK,
            slack_webhook_id: channel.slack_webhook_id ?? '',
          }
        : channel?.type === WORKFLOW_CHANNEL_TYPE.MAIL
          ? {
              type: WORKFLOW_CHANNEL_TYPE.MAIL,
              mail_address: channel.mail_address ?? '',
            }
          : { type: WORKFLOW_CHANNEL_TYPE.SLACK, slack_webhook_id: '' };

    return {
      title: initialState.title,
      channel: formChannel,
      basis: realtime.basis as WORKFLOW_REALTIME_BASIS,
      filterset: (realtime.filterset ?? {}) as Record<string, unknown>,
    };
  };

  // 저장 핸들러
  const handleSave = async (values: RealtimeFormData) => {
    const payload = {
      title: values.title,
      enable,
      type: WORKFLOW_TYPE.REALTIME,
      channel: values.channel,
      realtime: {
        basis: values.basis,
        filterset: values.filterset,
      },
    };

    if (mode === 'CREATE') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast('Workflow created', 'success');
          wr.push('/workflow');
        },
        onError: (error) => {
          toast(errorHandler(error, 'Failed to create workflow'), 'fail');
        },
      });
    } else {
      updateMutation.mutate(
        {
          notification_id: initialState!.alias,
          ...payload,
        },
        {
          onSuccess: () => {
            toast('Workflow updated', 'success');
            wr.push('/workflow');
          },
          onError: (error) => {
            toast(errorHandler(error, 'Failed to update workflow'), 'fail');
          },
        },
      );
    }
  };

  const form = useAppForm({
    ...realtimeFormOptions,
    defaultValues: getDefaultValues(),
    onSubmit: async ({ value }) => {
      await handleSave(value);
    },
  });

  // 토글 핸들러
  const handleToggle = async (status: boolean) => {
    if (!initialState) return;
    try {
      await enableNotification({
        notification_id: initialState.alias,
        enable: status,
      });
      setEnable(status);
      toast(status ? 'Workflow enabled' : 'Workflow disabled', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to toggle workflow'), 'fail');
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden  max-w-[1400px]">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-2 mb-6">
        <Button
          size="icon"
          variant="outline"
          onClick={() => wr.push('/workflow')}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-6">
          {mode === 'EDIT' && (
            <>
              <Switch checked={enable} onCheckedChange={handleToggle} />
              <Button variant="ghost" size="icon" onClick={onDelete}>
                <Trash2 className="size-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 본문 */}
      <RealtimeFormContent
        form={form}
        workspaceId={workspaceId}
        renderFooter={() => (
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <div className="flex gap-2">
                <Button
                  className="w-[100px]"
                  onClick={form.handleSubmit}
                  disabled={!canSubmit || isSubmitting}
                >
                  Save
                </Button>
              </div>
            )}
          </form.Subscribe>
        )}
      />
    </div>
  );
};

export default RealtimeWorkflowDetail;
